import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { MerchantLocation } from "@tdelivr/api-client/generated/commerce/merchant-location.js";

import { boundedNote } from "../board.ts";
import {
  BOARD_NOTE,
  branchRow,
  HOURS_REFUSAL,
  LISTING_NOTE,
  LOCATIONS_SCOPE,
  PER_FETCH,
  stateOf,
  statesPresent,
} from "../branches.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `hours` in `22 WP21 Merchant Portal.dc.html`: where this shop trades from.
 *
 * **The recorded blocker was half wrong and it is the wrong half that matters.** It said nothing
 * publishes a branch's opening hours *or its usual preparation time*, and called that "a field
 * absence and not a route absence". The preparation time has been on `merchant_locations` since the
 * table's first migration; what was missing was **the listing** — every MER-02 operation was
 * addressed by `locationId`, so a screen could change a branch it already had an identifier for and
 * could not discover one. ADR 0087 publishes it.
 *
 * What is drawn and what is not — `../branches.ts` argues each:
 *
 * | drawn                            | source                                                    |
 * | -------------------------------- | --------------------------------------------------------- |
 * | the rows                         | MER-02 `GET /v1/merchants/{merchantId}/locations`         |
 * | `Branch` — `Kilimani`            | `name`. A real name, like the products board's            |
 * | `Hours today` — `07:00–22:00`    | **nothing** — no service here holds an opening hour       |
 * | `Prep time` — `20–30 min`        | `preparationMinutes`, one figure where the design draws a range |
 * | `done · open`, `closed · closed` | `status`                                                  |
 * | `caution · busy mode`            | **nothing** — no busy mode is modelled                    |
 * | the `Mashujaa Day` row           | **not drawn** — a holiday is not a branch                 |
 * | the four tiles                   | no listing publishes a total (R-04); two are not counts   |
 * | the primary command              | drawn and refused — `HOURS_REFUSAL`                       |
 *
 * **This is the design's own chip corrected.** It reads `COM-01 · GEO`, and neither answers the
 * page: COM-01 holds catalogues, and there is no service called GEO. MER-02 holds branches, which
 * is what the board is of — the same correction R-17 made for the tenant console's service
 * catalogue, where the design cited two codes no contract answers and a third service drew the page.
 *
 * **It reads the merchant and not the branch**, like the staff board beside it: this is the shop's
 * list of branches, so sending a `locationId` would answer the one the token already names.
 */

const merchantLocation = services.commerce__merchant_location;

export interface BranchesAndHoursProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `EAM › Shop › Branches and hours`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Branches and hours";
/** MER-02, not the design's `COM-01 · GEO` — see this file's head. */
const SOURCE = "MER-02";
const DESCRIPTION = "Where you trade from, when you are open, and how busy you are.";
const COLUMNS = ["Branch", "Hours today", "Prep time"] as const;
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's own `FORMS.hours[0]`, which is the word on its heading button. */
const PRIMARY = "Set hours";

export function BranchesAndHours({ transport, session, crumbs }: BranchesAndHoursProps): ReactNode {
  const [rows, setRows] = useState<readonly MerchantLocation[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    if (!session.scopes.has(LOCATIONS_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [LOCATIONS_SCOPE] });
      return;
    }

    /*
     * The merchant, from the token, and nothing else. The listing is bounded by the verified tenant
     * in the service's own predicate, so a merchant identifier somebody else held would answer
     * their branches only if they were also in this tenant — and the claim is on the token rather
     * than in a path for the reason every merchant-held value on this portal is.
     */
    const merchantId = session.merchantId;
    if (merchantId === undefined) {
      setFailure({ kind: "forbidden", missingScopes: ["a merchant_id claim on your token"] });
      return;
    }

    try {
      const page = await merchantLocation
        .createMerchantLocationClient(transport)
        .listMerchantLocations(merchantId, String(PER_FETCH));
      setRows(page.merchantLocations);
      setTruncated(typeof page.nextAfter === "string" && page.nextAfter.length > 0);
    } catch (error) {
      setFailure(stateForError(error, [LOCATIONS_SCOPE]));
    }
  }, [transport, session.scopes, session.merchantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows.filter((row) => state === "" || stateOf(row).label === state).map(branchRow);

  const bounded = boundedNote(rows?.length, truncated, "branches", "in branch-code order");

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No branch is recorded for this shop yet. A row appears here once one is created with MER-02."
      primary={{ label: PRIMARY, refusal: HOURS_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: BOARD_NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote={LISTING_NOTE}
      exportName="branches-and-hours"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={BOARD_NOTE}
    />
  );
}
